import type { LibraryBlock } from "../build";
import {
  ellipse,
  icon,
  line,
  logo,
  motion,
  photo,
  pill,
  rect,
  SHADOW,
  stack,
  text,
  WHITE,
} from "./helpers";

// ---------------------------------------------------------------------------
// Intro & Outro: opening titles, end cards, "Danke fürs Zuschauen"
// ---------------------------------------------------------------------------

export const INTROS: LibraryBlock[] = [
  {
    id: "intro-logo",
    title: "Logo-Intro",
    category: "intro",
    tags: ["Logo", "Opener", "Einstieg", "präsentiert"],
    width: 800,
    height: 400,
    anchor: "center",
    duration: 3500,
    only: "video",
    nodes: [
      logo({
        x: 100,
        y: 30,
        w: 600,
        h: 220,
        on: "#000000",
        align: "center",
        ...motion("zoom", { duration: 800 }),
      }),
      line(250, 300, 300, WHITE, 6, motion("zoom", { delay: 450 })),
      text({
        text: "präsentiert",
        style: "subline",
        size: 38,
        color: WHITE,
        shadow: SHADOW,
        x: 0,
        y: 330,
        w: 800,
        align: "center",
        ...motion("fade", { delay: 650 }),
      }),
    ],
  },
  {
    id: "intro-willkommen",
    title: "Willkommen",
    category: "intro",
    tags: ["Hallo", "Begrüßung", "winken", "Einstieg"],
    width: 960,
    height: 440,
    anchor: "center",
    duration: 3500,
    only: "video",
    nodes: [
      icon({
        icon: "hand-waving",
        x: 400,
        y: 0,
        w: 160,
        h: 160,
        color: "accent",
        shadow: SHADOW,
        name: "Winken",
        ...motion("drop", { duration: 700, loop: "wiggle" }),
      }),
      stack({
        x: 0,
        y: 190,
        w: 960,
        align: "center",
        gap: 20,
        items: [
          {
            kind: "text",
            text: "Hallo & willkommen!",
            style: "heading-1",
            size: 84,
            color: WHITE,
            shadow: SHADOW,
            ...motion("pop", { delay: 200 }),
          },
          {
            kind: "text",
            text: "Schön, dass du da bist",
            style: "subline",
            size: 40,
            color: "#ffffffdd",
            shadow: SHADOW,
            ...motion("fade", { delay: 450 }),
          },
        ],
      }),
    ],
  },
  {
    id: "intro-folge",
    title: "Episoden-Intro",
    category: "intro",
    tags: ["Folge", "Episode", "Serie", "Podcast", "Datum"],
    width: 960,
    height: 420,
    anchor: "center",
    duration: 4000,
    only: "video",
    nodes: [
      ...pill(350, 0, 260, 64, "FOLGE 07", "accent", 28, motion("pop")),
      stack({
        x: 0,
        y: 100,
        w: 960,
        align: "center",
        gap: 24,
        items: [
          {
            kind: "text",
            text: "Mein Morgen als Bäckerin",
            style: "heading-1",
            size: 88,
            color: WHITE,
            shadow: SHADOW,
            ...motion("slide-up", { delay: 200, duration: 600 }),
          },
          {
            kind: "text",
            text: "Donnerstag, 12. Juni",
            style: "subline",
            size: 36,
            color: "#ffffffdd",
            shadow: SHADOW,
            ...motion("fade", { delay: 500 }),
          },
        ],
      }),
    ],
  },
  {
    id: "intro-action",
    title: "Filmklappe",
    category: "intro",
    tags: ["Action", "Dreh", "Behind the Scenes", "Film", "Klappe"],
    width: 800,
    height: 380,
    anchor: "center",
    duration: 3000,
    only: "video",
    nodes: [
      icon({
        icon: "film-slate",
        x: 300,
        y: 0,
        w: 200,
        h: 200,
        color: WHITE,
        shadow: SHADOW,
        name: "Klappe",
        ...motion("drop", { duration: 700 }),
      }),
      text({
        text: "Und … Action!",
        style: "heading-1",
        size: 100,
        color: WHITE,
        shadow: SHADOW,
        x: 0,
        y: 240,
        w: 800,
        align: "center",
        ...motion("pop", { delay: 450 }),
      }),
    ],
  },
  {
    id: "outro-bis-bald",
    title: "Bis bald",
    category: "intro",
    tags: ["Outro", "Ende", "Abschied", "Tschüss", "nächstes Video"],
    width: 960,
    height: 460,
    anchor: "center",
    duration: 4000,
    only: "video",
    nodes: [
      stack({
        x: 0,
        y: 230,
        w: 960,
        anchor: "center",
        align: "center",
        gap: [16, 28],
        items: [
          {
            kind: "text",
            text: "Bis bald!",
            style: "heading-1",
            size: 150,
            color: "accent",
            shadow: SHADOW,
            ...motion("pop", { duration: 600 }),
          },
          {
            kind: "text",
            text: "Neues Video jeden Dienstag",
            style: "subline",
            size: 42,
            color: WHITE,
            shadow: SHADOW,
            ...motion("slide-up", { delay: 300 }),
          },
          {
            kind: "text",
            text: "@deinkanal",
            style: "heading-2",
            size: 52,
            color: WHITE,
            shadow: SHADOW,
            ...motion("fade", { delay: 550 }),
          },
        ],
      }),
    ],
  },
  {
    id: "outro-kanaele",
    title: "Folge uns überall",
    category: "intro",
    tags: ["Outro", "Instagram", "TikTok", "YouTube", "Kanäle", "Social Media"],
    width: 900,
    height: 380,
    anchor: "center",
    duration: 4500,
    only: "video",
    nodes: [
      text({
        text: "Folge uns überall",
        style: "heading-2",
        size: 64,
        color: WHITE,
        shadow: SHADOW,
        x: 0,
        y: 0,
        w: 900,
        align: "center",
        ...motion("slide-down"),
      }),
      ...(["instagram-logo", "tiktok-logo", "youtube-logo"] as const).flatMap((name, index) => {
        const x = 175 + index * 200;
        const delay = 200 + index * 150;
        return [
          ellipse({
            x,
            y: 110,
            w: 150,
            h: 150,
            fill: "background",
            name: "Kreis",
            ...motion("pop", { delay }),
          }),
          icon({
            icon: name,
            x: x + 35,
            y: 145,
            w: 80,
            h: 80,
            color: "primary",
            ...motion("pop", { delay }),
          }),
        ];
      }),
      text({
        text: "@deinkanal",
        style: "heading-2",
        size: 52,
        color: WHITE,
        shadow: SHADOW,
        x: 0,
        y: 296,
        w: 900,
        align: "center",
        ...motion("fade", { delay: 700 }),
      }),
    ],
  },
  {
    id: "outro-endcard",
    title: "Endcard mit Videos",
    category: "intro",
    tags: ["Abspann", "Endscreen", "nächstes Video", "Abonnieren", "YouTube"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 8000,
    only: "video",
    nodes: ({ width, height }) => {
      // Two videos one below the other need a tall page (reels, stories).
      const portrait = height >= width * 1.5;
      const m = 90;
      const videoW = portrait ? width - m * 2 : (width - m * 3) / 2;
      const videoH = (videoW * 9) / 16;
      const top = portrait ? height * 0.12 : Math.max(m * 0.7, height * 0.08);
      const videosY = top + 130;
      const videos = portrait
        ? [
            { x: m, y: videosY },
            { x: m, y: videosY + videoH + 60 },
          ]
        : [
            { x: m, y: videosY },
            { x: m * 2 + videoW, y: videosY },
          ];
      const lastVideo = videos[videos.length - 1]!;
      const rowY = Math.min(height - 190, lastVideo.y + videoH + (portrait ? 110 : 80));
      return [
        rect({
          x: 0,
          y: 0,
          w: width,
          h: height,
          fill: "background/95",
          name: "Hintergrund",
          ...motion("fade", { duration: 400 }),
        }),
        text({
          text: "Schau als Nächstes",
          style: "heading-2",
          size: 64,
          color: "text",
          x: m,
          y: top,
          w: width - m * 2,
          align: "center",
          ...motion("slide-down", { delay: 200 }),
        }),
        ...videos.map((video, index) =>
          photo({
            x: video.x,
            y: video.y,
            w: videoW,
            h: videoH,
            radius: "md",
            name: `Video ${index + 1}`,
            ...motion("zoom", { delay: 350 + index * 200 }),
          }),
        ),
        logo({
          x: width / 2 - 360,
          y: rowY,
          w: 320,
          h: 110,
          on: "background",
          align: "right",
          ...motion("fade", { delay: 800 }),
        }),
        ...pill(
          width / 2 + 20,
          rowY,
          340,
          110,
          "Abonnieren",
          "primary",
          40,
          motion("pop", { delay: 900, loop: "pulse" }),
        ),
      ];
    },
  },
];
